'use client';
import React, { useState, useMemo, useEffect, useRef } from 'react';
import Link from 'next/link';
import { MapPin, Car, ArrowRight, Home, Building2, ChevronDown, ChevronUp } from 'lucide-react';
import { toSlug } from '@/lib/seo/slugs';
import { formatAed, formatCommuteTime } from '@/data/communityPrices';
import KhdaBadge from '@/components/ui/KhdaBadge';
import CurriculumBadge from '@/components/ui/CurriculumBadge';
import type { KhdaRating } from '@/components/ui/KhdaBadge';
import type { Curriculum } from '@/components/ui/CurriculumBadge';
import { SCHOOLS } from '@/data/schools';
import { trackCommunitySearch } from '@/lib/analytics/ga4Events';

interface CommunityCommute {
  name: string;
  travelMins: number;
  propertyType: string;
  avgAnnualRentAed: number;
  avgSalePriceAed: number;
}

interface Props {
  schoolId: string;
  schoolName: string;
  schoolSlug: string;
  schoolLocation: string;
  schoolCurriculum: Curriculum;
  schoolKhdaRating: KhdaRating;
  schoolFeeMin: number;
  schoolFeeMax: number;
  schoolAgeRange: string;
  communitiesWithCommute: CommunityCommute[];
}

const COMMUTE_OPTIONS = [10, 15, 20, 25, 30, 45];

function commuteColor(mins: number): string {
  if (mins <= 10) return '#16a34a';
  if (mins <= 20) return '#2563eb';
  if (mins <= 30) return '#d97706';
  return '#dc2626';
}

export default function CommunitiesNearClient({
  schoolId, schoolName, schoolSlug, schoolLocation,
  schoolCurriculum, schoolKhdaRating, schoolFeeMin, schoolFeeMax, schoolAgeRange,
  communitiesWithCommute,
}: Props) {
  const [maxCommute, setMaxCommute] = useState(30);
  const [showFilters, setShowFilters] = useState(false);

  // Fire community_search once when this page loads (Communities Near a School feature)
  const communitySearchFiredRef = useRef<string>('');
  useEffect(() => {
    if (communitySearchFiredRef.current === schoolId) return;
    communitySearchFiredRef.current = schoolId;
    trackCommunitySearch({
      school_name: schoolName,
      results_count: communitiesWithCommute.length,
    });
  }, [schoolId, schoolName, communitiesWithCommute.length]);

  // Use RAW travelMins for filtering, formatCommuteTime for display
  const filtered = useMemo(() => {
    return communitiesWithCommute.filter(c => c.travelMins <= maxCommute);
  }, [communitiesWithCommute, maxCommute]);

  const searchUrl = `/school-search-discovery?school=${schoolId}`;

  // Nearby schools for internal linking (other schools in same area)
  const nearbySchools = useMemo(() => {
    return SCHOOLS
      .filter(s => s.id !== schoolId)
      .slice(0, 6);
  }, [schoolId]);

  return (
    <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-20">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-xs text-muted-foreground mb-4" aria-label="Breadcrumb">
        <Link href="/" className="hover:text-primary flex items-center gap-1"><Home size={12} />Home</Link>
        <span>/</span>
        <Link href="/school-search-discovery" className="hover:text-primary">Schools</Link>
        <span>/</span>
        <Link href={`/school-profile-comparison?school=${schoolId}`} className="hover:text-primary">{schoolName}</Link>
        <span>/</span>
        <span className="text-foreground font-medium">Nearby Communities</span>
      </nav>

      {/* Page Header */}
      <header className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
          Communities Near {schoolName}
        </h1>
        <p className="text-muted-foreground text-sm sm:text-base mb-3">
          Considering {schoolName}? Discover Dubai communities within a convenient commute of the school.
        </p>
        <div className="inline-flex items-center gap-2 bg-primary/8 border border-primary/20 rounded-lg px-3 py-1.5 text-sm font-medium text-primary mb-4">
          <span className="font-semibold">Find the right school. Find the right community.</span>
        </div>

        {/* School summary card */}
        <div className="bg-white border border-border rounded-xl p-4">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="font-semibold text-foreground text-sm">{schoolName}</span>
            <KhdaBadge rating={schoolKhdaRating} size="sm" />
            <CurriculumBadge curriculum={schoolCurriculum} size="sm" />
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><MapPin size={11} />{schoolLocation}</span>
            <span>Ages: {schoolAgeRange}</span>
            <span>Fees: AED {schoolFeeMin.toLocaleString()} – {schoolFeeMax.toLocaleString()}</span>
          </div>
          <div className="mt-3 flex gap-2">
            <Link
              href={`/school-profile-comparison?school=${schoolId}`}
              className="text-xs font-medium text-primary hover:underline flex items-center gap-1"
            >
              Explore This School <ArrowRight size={11} />
            </Link>
          </div>
        </div>
      </header>

      {/* Commute filter */}
      <section className="bg-white border border-border rounded-xl mb-6 overflow-hidden">
        <button
          onClick={() => setShowFilters(v => !v)}
          className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-foreground hover:bg-muted/30 transition-colors"
          aria-expanded={showFilters}
        >
          <span>Filter by Commute</span>
          {showFilters ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
        {showFilters && (
          <div className="px-4 pb-4 border-t border-border pt-4">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Maximum Commute to School</p>
            <div className="flex flex-wrap gap-2">
              {COMMUTE_OPTIONS.map(m => (
                <button
                  key={m}
                  onClick={() => setMaxCommute(m)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    maxCommute === m
                      ? 'bg-primary text-white border-primary' :'bg-white text-foreground border-border hover:border-primary/50'
                  }`}
                >
                  ≤ {m} min
                </button>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Results count */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm font-semibold text-foreground">
          {filtered.length} communit{filtered.length !== 1 ? 'ies' : 'y'} within {maxCommute} min of {schoolName}
        </p>
        <Link
          href={searchUrl}
          className="text-xs text-primary hover:underline font-medium"
        >
          Open in full search →
        </Link>
      </div>

      {/* Community Cards */}
      <div className="space-y-3 mb-8">
        {filtered.length === 0 && (
          <div className="text-center py-12 text-muted-foreground text-sm">
            No communities found within {maxCommute} minutes. Try increasing the commute time.
          </div>
        )}
        {filtered.map(community => {
          const communitySlug = toSlug(community.name);
          return (
            <article
              key={community.name}
              className="bg-white border border-border rounded-xl p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <Link
                      href={`/schools-near/${communitySlug}`}
                      className="text-base font-semibold text-foreground hover:text-primary transition-colors"
                    >
                      {community.name}
                    </Link>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground mb-2">
                    <span className="flex items-center gap-1"><Building2 size={11} />{community.propertyType}</span>
                    {community.avgAnnualRentAed > 0 && (
                      <span>Rent: {formatAed(community.avgAnnualRentAed)}/yr</span>
                    )}
                    {community.avgSalePriceAed > 0 && (
                      <span>Sale: {formatAed(community.avgSalePriceAed)}</span>
                    )}
                  </div>
                  <Link
                    href={`/schools-near/${communitySlug}`}
                    className="text-xs text-primary hover:underline"
                  >
                    View schools near {community.name} →
                  </Link>
                </div>

                {/* Commute pill */}
                <div className="flex-shrink-0">
                  <div
                    className="flex items-center gap-1 px-2.5 py-1 rounded-full text-white text-xs font-bold"
                    style={{ backgroundColor: commuteColor(community.travelMins) }}
                  >
                    <Car size={11} />
                    {formatCommuteTime(community.travelMins)}
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {/* Unique content section */}
      <section className="bg-muted/30 border border-border rounded-xl p-5 mb-8">
        <h2 className="text-base font-semibold text-foreground mb-2">
          Where should I live near {schoolName}?
        </h2>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Use Schoolmapper to compare residential communities around {schoolName} and see how location could affect the daily school commute.
        </p>
      </section>

      {/* CTAs */}
      <div className="flex flex-col sm:flex-row gap-3 mb-10">
        <Link
          href={searchUrl}
          className="flex-1 flex items-center justify-center gap-2 bg-primary text-white rounded-xl px-5 py-3.5 text-sm font-semibold hover:bg-primary/90 transition-colors"
        >
          Explore Communities Near {schoolName}
          <ArrowRight size={16} />
        </Link>
        <Link
          href={`/school-profile-comparison?school=${schoolId}`}
          className="flex items-center justify-center gap-2 border border-border rounded-xl px-5 py-3.5 text-sm font-medium text-foreground hover:bg-muted/30 transition-colors"
        >
          Explore This School
        </Link>
      </div>

      {/* Internal links to other schools */}
      <section className="mt-4">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
          Explore other schools
        </h2>
        <div className="flex flex-wrap gap-2">
          {nearbySchools.map(s => (
            <Link
              key={s.id}
              href={`/communities-near/${toSlug(s.name)}`}
              className="text-xs px-3 py-1.5 rounded-full border border-border hover:border-primary/50 hover:text-primary transition-colors text-muted-foreground"
            >
              {s.shortName}
            </Link>
          ))}
        </div>
      </section>
    </main>
  );
}
